import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import styled from 'styled-components';

import { SrOnly } from '@/components/Layout/Parts';
import { SortableTh, Table, TableContent, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import ChannelsTooltip from '@/pages/DistributionSettings/Groups/ChannelsTooltip';
import { ChannelGroupResource, SortState } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import CopyButton from './CopyButton';

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.ASC,
  property: 'name',
};

const StyledTable = styled(Table)`
  th,
  td {
    padding-bottom: 16px;
    vertical-align: top;
    &:nth-child(1) {
      width: 35%;
      padding-left: 30px;
    }
    &:nth-child(2) {
      width: 20%;
    }
    &:nth-child(3) {
      width: 30%;
    }
    &:nth-child(4) {
      width: 15%;
      text-align: right;
    }
  }
  td {
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
  }
`;

const Channel = styled.div`
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ChannelIcon = styled.img`
  width: 16px;
  height: 16px;
  object-fit: contain;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background-color: #ecbe19;
`;

const GroupsTable = () => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const {
    data: groups,
    isLoading,
    isError,
  } = useQuery(
    ['channel_groups', { sort }],
    () =>
      api.get<never, ChannelGroupResource[]>(
        '/v1/distribution/channel-groups',
        {
          params: {
            ...(sort.direction !== 'DEFAULT' && {
              sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
            }),
          },
        },
      ),
    {
      staleTime: 120 * 1000,
    },
  );
  return (
    <StyledTable $fixed>
      <thead>
        <tr>
          <SortableTh
            property="name"
            title="Group Name"
            setSort={setSort}
            sort={sort}
          />
          <SortableTh property="id" title="ID" setSort={setSort} sort={sort} />
          <Th>Pages in Group</Th>
          <Th>
            <SrOnly>Actions</SrOnly>
          </Th>
        </tr>
      </thead>
      <tbody>
        <TableContent
          isLoading={isLoading}
          hasData={!!groups?.length}
          isError={isError}
        >
          {groups?.map((group) => (
            <tr key={group.id}>
              <Td>{group.name}</Td>
              <Td>{group.id}</Td>
              <Td>
                {!!group.channels?.length ? (
                  <>
                    {group.channels.slice(0, 3).map((channel) => (
                      <Channel key={channel.id}>
                        {!!channel.thumbnail && (
                          <ChannelIcon
                            src={channel.thumbnail}
                            alt={`${channel.name} icon`}
                          />
                        )}
                        <Text as="span" $type="smallBold">
                          {channel.name}
                        </Text>
                      </Channel>
                    ))}
                    {group.channels.length > 3 && (
                      <ChannelsTooltip channels={group.channels.slice(3)} />
                    )}
                  </>
                ) : (
                  '-'
                )}
              </Td>
              <Td>
                <CopyButton idToCopy={group.id} />
              </Td>
            </tr>
          ))}
        </TableContent>
      </tbody>
    </StyledTable>
  );
};

export default GroupsTable;
